import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  BookOpen, ChevronRight, ChevronDown, ChevronUp, 
  Languages, Search, ExternalLink, Bookmark
} from 'lucide-react';
import api from '../utils/api';
import { useI18n } from '../contexts/I18nContext';

const GrammarTopics = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [language, setLanguage] = useState('hebrew');
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTopic, setSelectedTopic] = useState(null);

  useEffect(() => {
    fetchTopics();
  }, [language]);

  const fetchTopics = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/grammar/topics/${language}`);
      setTopics(response.data.topics);
      
      // Expand all categories by default
      const expanded = {};
      response.data.topics.forEach((cat, idx) => {
        expanded[idx] = true;
      });
      setExpandedCategories(expanded);
    } catch (err) {
      setError('Failed to load grammar topics');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (index) => {
    setExpandedCategories(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const filteredTopics = topics.map(category => ({
    ...category,
    items: category.items.filter(item => 
      searchQuery === '' ||
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (item.hebrew_name && item.hebrew_name.includes(searchQuery)) ||
      (item.greek_name && item.greek_name.includes(searchQuery)) ||
      (item.description && item.description.toLowerCase().includes(searchQuery.toLowerCase()))
    )
  })).filter(category => category.items.length > 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <BookOpen className="w-10 h-10 text-amber-500" />
            <h1 className="text-4xl font-bold text-white">{t('grammar.title')}</h1>
          </div>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            {t('grammar.subtitle')}
          </p>
        </div>

        {/* Controls */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          {/* Language Toggle */}
          <div className="flex items-center bg-slate-800 rounded-lg p-1">
            <button
              onClick={() => setLanguage('hebrew')}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm transition-colors
                ${language === 'hebrew' 
                  ? 'bg-amber-500 text-slate-900' 
                  : 'text-slate-400 hover:text-white'}`}
            >
              <span className="fi fi-il"></span>
              <span>{t('grammar.biblicalHebrew')}</span>
            </button>
            <button
              onClick={() => setLanguage('greek')}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm transition-colors
                ${language === 'greek' 
                  ? 'bg-amber-500 text-slate-900' 
                  : 'text-slate-400 hover:text-white'}`}
            >
              <span className="fi fi-gr"></span>
              <span>{t('grammar.biblicalGreek')}</span>
            </button>
          </div>

          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder={t('grammar.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-amber-500"
            />
          </div>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 mb-6 text-red-400">
            {error}
          </div>
        )}

        {/* Topics List */}
        <div className="space-y-4">
          {filteredTopics.map((category, catIndex) => (
            <div 
              key={catIndex}
              className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 overflow-hidden"
            >
              {/* Category Header */}
              <button
                onClick={() => toggleCategory(catIndex)}
                className="w-full flex items-center justify-between p-4 hover:bg-slate-800/50 transition-colors"
              >
                <h2 className="text-lg font-semibold text-white">{category.category}</h2>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-400">{category.items.length} {t('grammar.topics')}</span>
                  {expandedCategories[catIndex] ? (
                    <ChevronUp className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  )}
                </div>
              </button>

              {/* Category Items */}
              {expandedCategories[catIndex] && (
                <div className="border-t border-slate-700">
                  {category.items.map((item, itemIndex) => (
                    <div
                      key={item.id}
                      className={`p-4 hover:bg-slate-900/50 transition-colors cursor-pointer border-b border-slate-700/50 last:border-b-0
                        ${selectedTopic?.id === item.id ? 'bg-slate-900/50' : ''}`}
                      onClick={() => setSelectedTopic(selectedTopic?.id === item.id ? null : item)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-white font-medium">{item.name}</h3>
                            {item.hebrew_name && (
                              <span className="text-amber-400 font-hebrew">{item.hebrew_name}</span>
                            )}
                            {item.greek_name && (
                              <span className="text-purple-400">{item.greek_name}</span>
                            )}
                          </div>
                          <p className="text-sm text-slate-400 line-clamp-2">{item.description}</p>
                          
                          {item.meaning && (
                            <span className="inline-block mt-2 text-xs px-2 py-1 bg-slate-700 rounded text-slate-300">
                              {item.meaning}
                            </span>
                          )}
                        </div>
                        <ChevronRight className={`w-5 h-5 text-slate-500 transition-transform
                          ${selectedTopic?.id === item.id ? 'rotate-90' : ''}`} 
                        />
                      </div>

                      {/* Expanded Topic Detail */}
                      {selectedTopic?.id === item.id && (
                        <div className="mt-4 pt-4 border-t border-slate-700 space-y-4">
                          {/* Examples */}
                          {item.examples && item.examples.length > 0 && (
                            <div>
                              <h4 className="text-sm font-semibold text-slate-300 mb-2">{t('grammar.examples')}</h4>
                              <div className="space-y-1">
                                {item.examples.map((ex, i) => (
                                  <p key={i} className={`text-sm ${language === 'hebrew' ? 'font-hebrew text-right' : ''}`}>
                                    <span className="text-amber-400">•</span> {ex}
                                  </p>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Rules */}
                          {item.rules && item.rules.length > 0 && (
                            <div>
                              <h4 className="text-sm font-semibold text-slate-300 mb-2">{t('grammar.rules')}</h4>
                              <ul className="space-y-1 text-sm text-slate-400">
                                {item.rules.map((rule, i) => (
                                  <li key={i} className="flex items-start gap-2">
                                    <span className="text-green-400 mt-1">✓</span>
                                    <span>{rule}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Frequency */}
                          {item.frequency && (
                            <p className="text-sm text-slate-500">
                              <span className="text-slate-400">{t('grammar.frequency')}</span> {item.frequency}
                            </p>
                          )}

                          {/* Suffixes/Prefixes for verb forms */}
                          {item.suffixes && (
                            <div>
                              <h4 className="text-sm font-semibold text-slate-300 mb-2">{t('grammar.personSuffixes')}</h4>
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                                {Object.entries(item.suffixes).map(([key, value]) => (
                                  <div key={key} className="bg-slate-900/50 rounded p-2">
                                    <span className="text-amber-400">{key}:</span>{' '}
                                    <span className="text-slate-300">{value}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {item.prefixes && (
                            <div>
                              <h4 className="text-sm font-semibold text-slate-300 mb-2">{t('grammar.personPrefixes')}</h4>
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                                {Object.entries(item.prefixes).map(([key, value]) => (
                                  <div key={key} className="bg-slate-900/50 rounded p-2">
                                    <span className="text-amber-400">{key}:</span>{' '}
                                    <span className="text-slate-300">{value}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Link to find verses with this grammar */}
                          <Link
                            to={`/bible-courses?grammar=${item.id}`}
                            className="inline-flex items-center gap-1 text-sm text-amber-400 hover:text-amber-300"
                          >
                            <Bookmark className="w-4 h-4" />
                            {t('grammar.findVerses')}
                          </Link>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {filteredTopics.length === 0 && (
          <div className="text-center py-12 text-slate-400">
            {t('grammar.noTopics')} "{searchQuery}"
          </div>
        )}
      </div>
    </div>
  );
};

export default GrammarTopics;
